import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@/environments/environment';

export function getProcedureHeaders(): HttpHeaders {
  let headers = new HttpHeaders().set('Content-Type', 'application/json');
  const currentUser = localStorage.getItem('currentUser');
  if (currentUser) {
    const user = JSON.parse(currentUser);
    if (user.token) {
      headers = headers.set('Authorization', `${user.token}`);
    }
  }
  return headers;
}

/**
 * Nombres canónicos de catálogo que corresponden a CatalogName en la tabla Catalog.
 * Se usan en el endpoint GET /catalogs-data/:name
 */
export const CATALOG_NAMES = {
  departments:   'Departments',   // Áreas responsables (dirección/dependencia)
  documentType:  'TipoDocumento', // Tipo de documento que se obtiene
  document:      'Documento',     // Documentos / requisitos
} as const;

@Injectable({ providedIn: 'root' })
export class ProcedureService {
  private base = environment.API_URL;

  constructor(private http: HttpClient) {}

  // ── Catálogo de trámites ──────────────────────────────────────────────────

  list(params: { search?: string; areaId?: string; active?: string; limit?: number; offset?: number } = {}): Observable<any> {
    let p = new HttpParams();
    if (params.search)  p = p.set('search',  params.search);
    if (params.areaId)  p = p.set('areaId',  params.areaId);
    if (params.active !== undefined) p = p.set('active', params.active);
    if (params.limit  !== undefined) p = p.set('limit',  String(params.limit));
    if (params.offset !== undefined) p = p.set('offset', String(params.offset));
    return this.http.get<any>(`${this.base}/procedures`, { headers: getProcedureHeaders(), params: p });
  }

  getById(id: string): Observable<any> {
    return this.http.get<any>(`${this.base}/procedures/${id}`, { headers: getProcedureHeaders() });
  }

  getBySlug(slug: string): Observable<any> {
    return this.http.get<any>(`${this.base}/procedures/slug/${encodeURIComponent(slug)}`);
  }

  create(body: any): Observable<any> {
    return this.http.post<any>(`${this.base}/procedures`, body, { headers: getProcedureHeaders() });
  }

  update(id: string, body: any): Observable<any> {
    return this.http.put<any>(`${this.base}/procedures/${id}`, body, { headers: getProcedureHeaders() });
  }

  toggle(id: string): Observable<any> {
    return this.http.patch<any>(`${this.base}/procedures/${id}/toggle`, {}, { headers: getProcedureHeaders() });
  }

  // ── Requisitos ────────────────────────────────────────────────────────────

  listRequirements(procedureId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/procedures/${procedureId}/requirements`, { headers: getProcedureHeaders() });
  }

  saveRequirements(procedureId: string, items: any[]): Observable<any> {
    return this.http.put<any>(`${this.base}/procedures/${procedureId}/requirements/bulk`, items, { headers: getProcedureHeaders() });
  }

  deleteRequirement(procedureId: string, reqId: number): Observable<any> {
    return this.http.delete<any>(`${this.base}/procedures/${procedureId}/requirements/${reqId}`, { headers: getProcedureHeaders() });
  }

  // ── Formularios vinculados ────────────────────────────────────────────────

  listForms(procedureId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/procedures/${procedureId}/forms`, { headers: getProcedureHeaders() });
  }

  linkForm(procedureId: string, body: any): Observable<any> {
    return this.http.post<any>(`${this.base}/procedures/${procedureId}/forms`, body, { headers: getProcedureHeaders() });
  }

  unlinkForm(procedureId: string, formLinkId: number): Observable<any> {
    return this.http.delete<any>(`${this.base}/procedures/${procedureId}/forms/${formLinkId}`, { headers: getProcedureHeaders() });
  }

  updateForm(procedureId: string, formLinkId: number, body: any): Observable<any> {
    return this.http.put<any>(`${this.base}/procedures/${procedureId}/forms/${formLinkId}`, body, { headers: getProcedureHeaders() });
  }

  // ── Catálogos de soporte (usa el endpoint existente /catalogs-data/:name) ──

  /**
   * @param catalogName  Valor de CatalogName en la tabla Catalog.
   *                     Ejemplos: 'Departments', 'TipoDocumento', 'Documento'
   */
  getCatalogByName(catalogName: string, limit = 200, offset = 0): Observable<any> {
    const params = new HttpParams()
      .set('limit',  String(limit))
      .set('offset', String(offset));
    return this.http.get<any>(`${this.base}/catalogs-data/${encodeURIComponent(catalogName)}`,
      { headers: getProcedureHeaders(), params });
  }

  createCatalogItem(catalogName: string, body: any): Observable<any> {
    return this.http.post<any>(
      `${this.base}/catalogs-data/${encodeURIComponent(catalogName)}`,
      body,
      { headers: getProcedureHeaders() }
    );
  }

  updateCatalogItem(catalogName: string, id: number, body: any): Observable<any> {
    return this.http.put<any>(
      `${this.base}/catalogs-data/${encodeURIComponent(catalogName)}/${id}`,
      body,
      { headers: getProcedureHeaders() }
    );
  }

  deleteCatalogItem(catalogName: string, id: number): Observable<any> {
    return this.http.delete<any>(
      `${this.base}/catalogs-data/${encodeURIComponent(catalogName)}/${id}`,
      { headers: getProcedureHeaders() }
    );
  }

  // ── Catálogo público (sin auth) ───────────────────────────────────────────

  /**
   * Obtiene entradas del catálogo usando el endpoint público GET /catalogs/:name.
   * No requiere sesión activa (usado en formularios de ciudadano anónimo).
   */
  getPublicCatalog(catalogName: string, limit = 500): Observable<any[]> {
    return this.http.get<any>(`${this.base}/catalogs/${encodeURIComponent(catalogName)}?limit=${limit}`).pipe(
      map((res: any) => {
        if (Array.isArray(res)) return res;
        if (res && Array.isArray(res.list)) return res.list;
        if (res && Array.isArray(res.Results)) return res.Results;
        return [];
      })
    );
  }

  // ── Surveys disponibles para vincular ─────────────────────────────────────

  listSurveys(): Observable<any[]> {
    const p = new HttpParams().set('page', '1').set('limit', '500');
    return this.http
      .get<any>(`${this.base}/surveys`, { headers: getProcedureHeaders(), params: p })
      .pipe(map((res: any) => (Array.isArray(res) ? res : (res?.data ?? []))));
  }

  // ── Solicitudes ciudadanas ────────────────────────────────────────────────

  listRequests(params: { status?: string; procedureId?: string; limit?: number; offset?: number } = {}): Observable<any> {
    let p = new HttpParams();
    if (params.status)      p = p.set('status',      params.status);
    if (params.procedureId) p = p.set('procedureId', params.procedureId);
    if (params.limit  !== undefined) p = p.set('limit',  String(params.limit));
    if (params.offset !== undefined) p = p.set('offset', String(params.offset));
    return this.http.get<any>(`${this.base}/procedure-requests`, { headers: getProcedureHeaders(), params: p });
  }

  /** Busca borrador existente de un ciudadano para un trámite (devuelve null si no existe) */
  getDraftRequest(procedureId: string, userId: string): Observable<any> {
    return this.http.get<any>(
      `${this.base}/procedures/${procedureId}/requests/draft?userId=${userId}`
    );
  }

  /** Crea una solicitud en estado Borrador */
  createRequest(procedureId: string, userId: string): Observable<any> {
    return this.http.post<any>(
      `${this.base}/procedures/${procedureId}/requests`,
      { UserId: userId }
    );
  }

  /** Obtiene el detalle de una solicitud con respuestas y documentos */
  getRequest(requestId: string): Observable<any> {
    return this.http.get<any>(`${this.base}/procedure-requests/${requestId}`);
  }

  /** Guarda las respuestas del formulario */
  saveAnswers(requestId: string, answers: { SurveyQuestionId: number; Answer: string }[]): Observable<any> {
    return this.http.post<any>(
      `${this.base}/procedure-requests/${requestId}/answers`,
      answers
    );
  }

  /** Sube un documento para un requisito */
  uploadDocument(requestId: string, requirementId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('requirementId', String(requirementId));
    return this.http.post<any>(
      `${this.base}/procedure-requests/${requestId}/documents`,
      formData
    );
  }

  /** Elimina un documento cargado */
  deleteDocument(requestId: string, documentId: number): Observable<any> {
    return this.http.delete<any>(
      `${this.base}/procedure-requests/${requestId}/documents/${documentId}`
    );
  }

  /**
   * Descarga un documento como Blob via stream autenticado.
   * Usar con: service.viewDocument(rid, did).subscribe(blob => { ... })
   */
  viewDocument(requestId: string, documentId: number): Observable<Blob> {
    const token = (() => {
      try { return JSON.parse(localStorage.getItem('currentUser') || '{}')?.token || ''; }
      catch { return ''; }
    })();
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.get(
      `${this.base}/procedure-requests/${requestId}/documents/${documentId}/file`,
      { headers, responseType: 'blob' }
    );
  }

  /** Cambia el estado de la solicitud a Enviado */
  submitRequest(requestId: string): Observable<any> {
    return this.http.put<any>(
      `${this.base}/procedure-requests/${requestId}/submit`,
      {}
    );
  }

  /** Lista solicitudes de un ciudadano específico (sin auth) */
  listRequestsByUser(userId: string, params: { status?: string; limit?: number; offset?: number } = {}): Observable<any> {
    let p = new HttpParams();
    if (params.status) p = p.set('status', params.status);
    if (params.limit  !== undefined) p = p.set('limit',  String(params.limit));
    if (params.offset !== undefined) p = p.set('offset', String(params.offset));
    return this.http.get<any>(`${this.base}/procedure-requests/by-user/${userId}`, { params: p });
  }

  /** Vincula un ciudadano (userId) a una solicitud en borrador */
  updateRequestUser(requestId: string, userId: string): Observable<any> {
    return this.http.patch<any>(
      `${this.base}/procedure-requests/${requestId}/user`,
      { UserId: userId }
    );
  }

  /** Registra el inicio de captura al retomar un borrador (preserva el primer inicio) */
  startRequest(requestId: string): Observable<any> {
    return this.http.patch<any>(
      `${this.base}/procedure-requests/${requestId}/start`,
      {}
    );
  }

  /** Envía observaciones al ciudadano por correo */
  respondCitizen(requestId: string, body: {
    message: string;
    items: { label: string; comment: string; answerId?: number; documentId?: number }[];
  }): Observable<any> {
    return this.http.post<any>(`${this.base}/procedure-requests/${requestId}/respond`, body);
  }

  /** Aprueba la solicitud */
  approveRequest(requestId: string, notes?: string): Observable<any> {
    return this.http.patch<any>(`${this.base}/procedure-requests/${requestId}/approve`, { notes: notes || '' });
  }

  /** Rechaza la solicitud con un motivo */
  rejectRequest(requestId: string, reason: string): Observable<any> {
    return this.http.patch<any>(`${this.base}/procedure-requests/${requestId}/reject`, { reason });
  }

  /** Sube el documento obtenido al aprobar (admin) */
  uploadOutputDocument(requestId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<any>(
      `${this.base}/procedure-requests/${requestId}/output-document`,
      formData,
      { headers: getProcedureHeaders() }
    );
  }

  /** Descarga un documento como Blob (ciudadano o admin autenticado) */
  downloadDocumentBlob(requestId: string, documentId: number): Observable<Blob> {
    return this.viewDocument(requestId, documentId);
  }
}
